import * as THREE from 'three';
import type { InputState } from '@moba/shared';
import { nextAutoTarget } from './targeting.ts';

/** Algo seleccionable con clic izquierdo: héroe, minion o estructura enemiga. */
export interface PickTarget {
  id: number;
  pos: { x: number; z: number };
  radius: number;
  alive: boolean;
  /** altura del eje vertical usado para el picking */
  height: number;
  /** margen extra de clic alrededor del cuerpo (más grande = más fácil de seleccionar) */
  pickPadding?: number;
}

export class Input {
  readonly aim = { x: 0, z: 1 };
  readonly moveTarget = { x: 0, z: 0, active: false };
  moveTargetSerial = 0;
  /** rango de habilidad pedido (botón + o Shift+tecla), se envía en el próximo sample */
  private pendingLevelUp: 'q' | 'w' | 'e' | 'r' | '' = '';
  /** Enemigo fijado con clic izquierdo (0 = ninguno). */
  targetId = 0;
  targetSerial = 0;
  screenX = 0;
  screenY = 0;
  enabled = true;
  /** Se llama tras cada orden (Q/W/E/R, B, clic, rango, compra): online envía la entrada al momento. */
  onCommand?: () => void;
  /**
   * Controles táctiles (móvil): el puntero no apunta (no hay cursor); se mueve
   * con el joystick (`setJoystick`) y las habilidades apuntan con `castAt`.
   */
  touchMode = false;
  /** dirección del joystick en el suelo (normalizada) o null si no se toca */
  private joystick: { x: number; z: number } | null = null;

  private readonly keys = new Set<string>();
  private readonly pressed = new Set<string>();
  private readonly raycaster = new THREE.Raycaster();
  private readonly ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private readonly ndc = new THREE.Vector2();
  private readonly hit = new THREE.Vector3();
  private lastCamera: THREE.Camera | null = null;
  private rightDown = false;
  private playerX = 0;
  private playerZ = 0;
  private enemies: readonly PickTarget[] = [];
  private selectables: readonly PickTarget[] = [];
  /** unidad inspeccionada en el panel de estadísticas (0 = el propio héroe) */
  selectedId = 0;
  private readonly segA = new THREE.Vector3();
  private readonly segB = new THREE.Vector3();

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.screenX = window.innerWidth / 2;
    this.screenY = window.innerHeight / 2;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('mouseup', this.onMouseUp);
    canvas.addEventListener('mousedown', this.onMouseDown);
    canvas.addEventListener('contextmenu', this.onContextMenu);
  }

  /** radio para pasar al siguiente enemigo cuando muere el objetivo (alcance + margen) */
  autoAcquireRange = 6;

  setPlayerPosition(x: number, z: number): void {
    this.playerX = x;
    this.playerZ = z;
  }

  /** Unidades seleccionables con clic izquierdo (enemigas del jugador). */
  setEnemies(enemies: readonly PickTarget[]): void {
    this.enemies = enemies;
  }

  private pendingBuy = '';
  private pendingSell = -1;

  requestBuy(itemId: string): void {
    this.pendingBuy = itemId;
    this.onCommand?.();
  }

  requestSell(slot: number): void {
    this.pendingSell = slot;
    this.onCommand?.();
  }

  requestLevelUp(slot: 'q' | 'w' | 'e' | 'r'): void {
    this.pendingLevelUp = slot;
    this.onCommand?.();
  }

  /** Orden de movimiento desde el minimapa (suelta el objetivo, como el clic derecho). */
  setMoveTarget(x: number, z: number): void {
    this.targetId = 0;
    this.moveTarget.x = x;
    this.moveTarget.z = z;
    this.moveTarget.active = true;
    this.moveTargetSerial++;
  }

  /** Todas las unidades (aliadas y enemigas) que se pueden inspeccionar con clic izquierdo. */
  setSelectables(units: readonly PickTarget[]): void {
    this.selectables = units;
  }

  clearTarget(): void {
    this.targetId = 0;
  }

  clearMoveTarget(): void {
    this.moveTarget.active = false;
  }

  clearPressed(): void {
    this.pressed.clear();
    this.rightDown = false;
  }

  updateAim(camera: THREE.Camera): void {
    this.lastCamera = camera;
    this.refreshAim();
  }

  private refreshAim(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.ndc.set(
      ((this.screenX - rect.left) / rect.width) * 2 - 1,
      -((this.screenY - rect.top) / rect.height) * 2 + 1,
    );
    // táctil: el punto de mira lo fija cada habilidad (`castAt`), no el último toque
    if (!this.lastCamera || this.touchMode) return;

    this.raycaster.setFromCamera(this.ndc, this.lastCamera);
    if (this.raycaster.ray.intersectPlane(this.ground, this.hit)) {
      this.aim.x = this.hit.x;
      this.aim.z = this.hit.z;
      if (this.rightDown) {
        this.moveTarget.x = this.aim.x;
        this.moveTarget.z = this.aim.z;
        this.moveTarget.active = true;
      }
    }
  }

  /**
   * Joystick táctil: mientras se mantiene, el héroe camina en esa dirección del
   * suelo (como una orden de movimiento continua, así la predicción online y el
   * `moveTo` del servidor funcionan igual). Al tocarlo se suelta el objetivo.
   */
  setJoystick(direction: { x: number; z: number } | null): void {
    if (direction && !this.joystick) {
      this.targetId = 0;
      this.onCommand?.();
    }
    if (!direction && this.joystick) this.moveTarget.active = false;
    this.joystick = direction;
  }

  /** Lanza una habilidad apuntada a un punto del suelo (botones táctiles). */
  castAt(slot: 'q' | 'w' | 'e' | 'r', x: number, z: number): void {
    this.aim.x = x;
    this.aim.z = z;
    this.pressed.add(`Key${slot.toUpperCase()}`);
    this.onCommand?.();
  }

  /** Volver a base desde un botón (como la tecla B). */
  pressRecall(): void {
    this.targetId = 0;
    this.moveTarget.active = false;
    this.pressed.add('KeyB');
    this.onCommand?.();
  }

  /** Fija el enemigo más cercano dentro de `range` (botón táctil de ataque). Devuelve su id o 0. */
  lockNearest(range: number): number {
    const id = nextAutoTarget(this.enemies, { x: this.playerX, z: this.playerZ }, range);
    if (id) {
      this.targetId = id;
      this.targetSerial++;
      this.moveTarget.active = false;
      this.selectedId = id;
      this.onCommand?.();
    }
    return id;
  }

  /** Enemigos seleccionables (los usa el apuntado táctil automático). */
  get enemyTargets(): readonly PickTarget[] {
    return this.enemies;
  }

  sample(): InputState {
    let moveX = 0;
    let moveZ = 0;

    if (this.joystick) {
      // un destino unos metros por delante: se renueva cada tick mientras se empuja
      this.moveTarget.x = this.playerX + this.joystick.x * 4;
      this.moveTarget.z = this.playerZ + this.joystick.z * 4;
      this.moveTarget.active = true;
    }

    if (this.moveTarget.active) {
      const dx = this.moveTarget.x - this.playerX;
      const dz = this.moveTarget.z - this.playerZ;
      const distance = Math.hypot(dx, dz);
      if (distance > 0.4) {
        moveX = dx / distance;
        moveZ = dz / distance;
      } else {
        this.moveTarget.active = false;
      }
    }

    if (this.targetId && !this.enemies.some((hero) => hero.id === this.targetId && hero.alive)) {
      // el objetivo murió (o ya no se puede atacar): pasar al enemigo más cercano
      const next = nextAutoTarget(this.enemies, { x: this.playerX, z: this.playerZ }, this.autoAcquireRange, this.targetId);
      if (next && this.selectedId === this.targetId) this.selectedId = next;
      this.targetId = next;
    }

    const input: InputState = {
      moveX,
      moveZ,
      // el destino viaja tal cual: la simulación frena con la posición real
      ...(this.moveTarget.active ? { moveTo: { x: this.moveTarget.x, z: this.moveTarget.z } } : {}),
      aimX: this.aim.x,
      aimZ: this.aim.z,
      attack: false,
      targetId: this.enabled ? this.targetId : 0,
      recall: this.enabled && this.pressed.has('KeyB'),
      levelUp: this.pendingLevelUp,
      buy: this.pendingBuy,
      sell: this.pendingSell,
      abilities: {
        q: this.enabled && this.pressed.has('KeyQ'),
        w: this.enabled && this.pressed.has('KeyW'),
        e: this.enabled && this.pressed.has('KeyE'),
        r: this.enabled && this.pressed.has('KeyR'),
      },
    };

    this.pressed.clear();
    this.pendingLevelUp = '';
    this.pendingBuy = '';
    this.pendingSell = -1;
    return input;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('mouseup', this.onMouseUp);
    this.canvas.removeEventListener('mousedown', this.onMouseDown);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.code === 'Space' || event.code === 'Tab') event.preventDefault();
    // Shift + Q/W/E/R sube el rango de la habilidad, no la lanza.
    // No se usa Ctrl: el navegador se queda con sus atajos (Ctrl+W, Ctrl+R...).
    const rankKeys: Record<string, 'q' | 'w' | 'e' | 'r'> = { KeyQ: 'q', KeyW: 'w', KeyE: 'e', KeyR: 'r' };
    if (event.shiftKey && !event.ctrlKey && !event.metaKey && rankKeys[event.code]) {
      event.preventDefault();
      if (!event.repeat) {
        this.pendingLevelUp = rankKeys[event.code];
        this.onCommand?.();
      }
      return;
    }
    if (event.code === 'KeyB' && !event.repeat) {
      // volver a base: soltar objetivo y destino para no cancelarlo al instante
      this.targetId = 0;
      this.moveTarget.active = false;
    }
    if (!event.repeat) this.pressed.add(event.code);
    this.keys.add(event.code);
    if (!event.repeat && (rankKeys[event.code] || event.code === 'KeyB')) this.onCommand?.();
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.keys.delete(event.code);
  };

  private readonly onBlur = (): void => {
    this.keys.clear();
    this.pressed.clear();
    this.rightDown = false;
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    // táctil: los dedos sobre el joystick/botones no son un cursor
    if (this.touchMode && event.pointerType !== 'mouse') return;
    this.screenX = event.clientX;
    this.screenY = event.clientY;
  };

  private readonly onMouseDown = (event: MouseEvent): void => {
    this.screenX = event.clientX;
    this.screenY = event.clientY;
    this.refreshAim();

    if (event.button === 0) {
      const picked = this.pickEnemy();
      if (picked) {
        this.targetId = picked.id;
        this.targetSerial++;
        this.moveTarget.active = false;
        this.selectedId = picked.id;
      } else {
        // aliados: sólo se inspeccionan; clic en el suelo vuelve al propio héroe
        this.selectedId = this.pickFrom(this.selectables)?.id ?? 0;
      }
    }
    if (event.button === 2) {
      event.preventDefault();
      // clic derecho sobre un enemigo = fijarlo y autoatacar (como el izquierdo)
      const picked = this.pickEnemy();
      if (picked) {
        this.targetId = picked.id;
        this.targetSerial++;
        this.moveTarget.active = false;
        this.rightDown = false;
        this.onCommand?.();
        return;
      }
      this.targetId = 0;
      this.rightDown = true;
      this.refreshAim();
      this.moveTarget.x = this.aim.x;
      this.moveTarget.z = this.aim.z;
      this.moveTarget.active = true;
      this.moveTargetSerial++;
    }
    if (event.button === 0 || event.button === 2) this.onCommand?.();
  };

  /** Rayo de cámara contra el eje vertical de cada enemigo vivo. */
  private pickEnemy(): PickTarget | undefined {
    return this.pickFrom(this.enemies);
  }

  private pickFrom(units: readonly PickTarget[]): PickTarget | undefined {
    if (!this.lastCamera) return undefined;
    this.raycaster.setFromCamera(this.ndc, this.lastCamera);
    const ray = this.raycaster.ray;
    let best: PickTarget | undefined;
    let bestDist = Infinity;
    for (const hero of units) {
      if (!hero.alive) continue;
      this.segA.set(hero.pos.x, 0, hero.pos.z);
      this.segB.set(hero.pos.x, hero.height, hero.pos.z);
      const dist = Math.sqrt(ray.distanceSqToSegment(this.segA, this.segB));
      const threshold = hero.radius + (hero.pickPadding ?? 0.45);
      if (dist <= threshold && dist < bestDist) {
        best = hero;
        bestDist = dist;
      }
    }
    return best;
  }

  private readonly onMouseUp = (event: MouseEvent): void => {
    if (event.button === 2) {
      event.preventDefault();
      this.rightDown = false;
    }
  };

  private readonly onContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
  };
}
